import { createContext, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  trans,
  useSoyuzFlag,
  useSoyuzExperiment,
  clientErrorTracker,
  useSoyuzBrandConfig
} from '@spotahome/soyuz/client';
import { formatPrice as formatPriceWithCurrency } from '@spotahome/soyuz-currency';

import { cityToggleConfig } from '@spotahome/ui-library';
import { isPlanWithUplift } from '@spotahome/ui-library/src/utils/plans';
import { dateIsBefore } from '@spotahome/ui-library/src/tenant/components/utils/DateRangeHandler';

import pricingShape from '../shapes/pricing';

import getGqlClient from '../graphql/client';
import { getQuotationQuery } from '../graphql/queries/getPricingQuery';
import { getRentableUnitPrice } from '../utils/paymentsAdapter';

const WEEKLY_PRICING = 'weekly';
const CONTRACT_DURATION_TEMPORAL = 'temporal';
const CONTRACT_DURATION_PERMANENT = 'permanent';

const checkHasNoDeposit = depositType => depositType === 'noSecurityDeposit';

const getInitialState = bookingConditions => {
  return {
    pricingDetails: {},
    downPaymentTimeInterval: {},
    detailedAdminFee: {
      depositType: bookingConditions.securityDepositType,
      depositValue: bookingConditions.securityDepositValue,
      feeValue: bookingConditions.adminFeeValue
    },
    hasNoDeposit: checkHasNoDeposit(bookingConditions.securityDepositType),
    isWeekly: false
  };
};

const checkIfDownPaymentIsWeekly = pricing =>
  pricing.downPaymentMaxCapType === WEEKLY_PRICING;

const getDownPaymentTimeInterval = (pricing, isWeekly) =>
  isWeekly
    ? {
        type: pricing.downPaymentMaxCapType,
        value: pricing.downPaymentMaxCapValue
      }
    : {};

async function getPricing({
  listingId,
  checkInDate,
  checkOutDate,
  tenantCommissionStrategy,
  downPaymentStrategy
}) {
  const contractDurationType = checkOutDate
    ? CONTRACT_DURATION_TEMPORAL
    : CONTRACT_DURATION_PERMANENT;

  const {
    data: { pricing }
  } = await getQuotationQuery(getGqlClient())({
    listingId,
    checkInDate,
    checkOutDate,
    contractDurationType,
    tenantCommissionStrategy,
    downPaymentStrategy
  });

  const pricingDetails = {
    pricing,
    bookingFee: pricing.tenantFeeGross,
    downPayment: pricing.downPayment,
    reservationPrice: pricing.reservationPrice,
    tenantPayments: pricing.tenantPayments.map(price => price / 100),
    adminFee: pricing.adminFee,
    hasOffer: !!pricing.offer
  };

  const detailedAdminFee = {
    depositType: pricing.securityDepositType,
    depositValue: pricing.securityDeposit,
    feeValue: pricing.adminFee,
    totalToPayOnMoveIn: pricing.securityDeposit + pricing.adminFee
  };

  const isWeekly = checkIfDownPaymentIsWeekly(pricing);

  return {
    isWeekly,
    pricingDetails,
    detailedAdminFee,
    hasNoDeposit: checkHasNoDeposit(pricing.securityDepositType),
    downPaymentTimeInterval: getDownPaymentTimeInterval(pricing, isWeekly)
  };
}

const usePriceRange = ({
  monthlyPrice,
  checkInDate,
  checkOutDate,
  currencyIsoCode
}) => {
  return getRentableUnitPrice(
    monthlyPrice.type,
    monthlyPrice.minimumPrice,
    currencyIsoCode,
    monthlyPrice.pricesByMonth,
    monthlyPrice.fixedPrice,
    { moveIn: checkInDate, moveOut: checkOutDate }
  );
};

export const PricingContext = createContext();

export const PricingProvider = ({
  children,
  allowsTemporalContract = false,
  allowsPermanentContract = false,
  listingPricing,
  bookingConditions,
  listingId,
  listingPlan,
  checkInDate = null,
  checkOutDate = null,
  cityId
}) => {
  const [pricing, setPricing] = useState(getInitialState(bookingConditions));
  const [isLoaded, setIsLoaded] = useState(false);
  const priceRange = usePriceRange({
    monthlyPrice: listingPricing.monthlyPrice,
    currencyIsoCode: listingPricing.currencyLabel,
    checkInDate,
    checkOutDate
  });
  const { id: brandId } = useSoyuzBrandConfig();

  const formatPrice = amount =>
    formatPriceWithCurrency(amount, listingPricing.currencyLabel);

  const downPaymentStrategyFlag = useSoyuzFlag(
    'FirstMonthDownPaymentStrategyEnabled'
  );

  const tenantFeeStrategyExperiment = useSoyuzExperiment(
    'TenantFeeWithoutUplift'
  );

  const isTenantFeeExperiment =
    cityToggleConfig(cityId).isTenantFeeExperimentCity &&
    isPlanWithUplift(listingPlan) &&
    brandId === 'spotahome';

  useEffect(
    () => {
      const getDownPaymentStrategy = () => {
        return downPaymentStrategyFlag.isOn()
          ? 'downPaymentFirstMonthStrategy'
          : 'downPaymentFirstFullMonthStrategy';
      };

      const getTenantCommissionStrategy = () => {
        const variant = isTenantFeeExperiment
          ? tenantFeeStrategyExperiment.get('behaviour')
          : 'original';
        return variant === 'original'
          ? 'downpaymentPercentage11thOptimized'
          : 'downpaymentPercentage11thOptimizedWithoutUplift';
      };

      const dateRangeIsCorrect =
        checkInDate && checkOutDate && dateIsBefore(checkInDate, checkOutDate);

      const updatePricing = async () => {
        if (
          !listingId ||
          !checkInDate ||
          (checkInDate && !checkOutDate && !allowsPermanentContract)
        ) {
          return;
        }

        if (dateRangeIsCorrect || (checkInDate && allowsPermanentContract)) {
          try {
            const pricingDataUpdatedAdminFee = await getPricing({
              listingId,
              checkInDate,
              checkOutDate: allowsTemporalContract ? checkOutDate : null,
              tenantCommissionStrategy: getTenantCommissionStrategy(),
              downPaymentStrategy: getDownPaymentStrategy()
            });
            setPricing(pricingDataUpdatedAdminFee);
            setIsLoaded(true);
          } catch (error) {
            setIsLoaded(false);
            clientErrorTracker.error({
              error,
              source: 'ListingPricingContext-Quotation'
            });
          }
        }
      };
      updatePricing();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkInDate, checkOutDate, allowsTemporalContract, listingId]
  );

  return (
    <PricingContext.Provider
      value={{
        ...pricing,
        priceRange,
        isMonthlyPrice: priceRange.type === 'byMonth',
        monthlyPrice: listingPricing.monthlyPrice,
        minimumPrice: listingPricing.monthlyPrice.minimumPrice,
        isLoaded,
        formatPrice,
        hasDates: !!checkInDate || !!checkOutDate
      }}
    >
      {children}
    </PricingContext.Provider>
  );
};

PricingProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  listingPricing: PropTypes.shape(pricingShape).isRequired,
  bookingConditions: PropTypes.shape({
    securityDepositValue: PropTypes.number,
    securityDepositType: PropTypes.string,
    adminFeeType: PropTypes.string,
    adminFeeValue: PropTypes.number
  }).isRequired,
  listingId: PropTypes.string.isRequired,
  listingPlan: PropTypes.string.isRequired,
  checkInDate: PropTypes.string,
  checkOutDate: PropTypes.string,
  allowsTemporalContract: PropTypes.bool,
  allowsPermanentContract: PropTypes.bool,
  cityId: PropTypes.string.isRequired
};

export const PricingConsumer = PricingContext.Consumer;

export const usePricingContext = () => useContext(PricingContext);

const getDisplayPriceRange = priceRange => {
  const hasPriceByMonth = priceRange.type === 'byMonth';

  const { seasonalPrice, fixedPrice } = priceRange;
  const isSeasonalPrice = !!priceRange.seasonalPrice && hasPriceByMonth;

  return isSeasonalPrice
    ? `${seasonalPrice.min}-${seasonalPrice.max}`
    : fixedPrice;
};

const getDisplayMinPrice = priceRange => {
  const hasPriceByMonth = priceRange.type === 'byMonth';
  const minPrice = priceRange.amount || priceRange.fixedPrice;

  return hasPriceByMonth && priceRange.seasonalPrice
    ? priceRange.seasonalPrice.min
    : minPrice;
};

export const useDisplayListingPricing = () => {
  const {
    priceRange,
    formatPrice,
    pricingDetails: { pricing, hasOffer }
  } = usePricingContext();

  if (hasOffer) {
    const { discountType, discountValue } = pricing.offer;
    return {
      ...pricing.offer,
      price: formatPrice(pricing.monthlyPrice),
      priceWithoutOffer: formatPrice(
        priceRange.isSeasonalPriceFeatureOn
          ? getDisplayPriceRange(priceRange)
          : pricing.monthlyPriceWithoutOffer
      ),
      discount:
        discountType === 'percentage'
          ? `${discountValue}%`
          : formatPrice(discountValue)
    };
  }

  const amount = priceRange.isSeasonalPriceFeatureOn
    ? getDisplayPriceRange(priceRange)
    : getDisplayMinPrice(priceRange);

  return { price: formatPrice(amount) };
};

export const useDisplayPriceIndex = m2 => {
  const { priceRange, formatPrice } = usePricingContext();

  if (!m2) {
    return {};
  }
  const priceIndex =
    Math.round((getDisplayMinPrice(priceRange) / m2) * 100) / 100;
  const isRange =
    priceRange.isSeasonalPriceFeatureOn && priceRange.type === 'byMonth';
  const prefix = isRange ? `${trans('alerts.alert.date.from')} ` : '';
  return {
    priceIndex: `${prefix}${formatPrice(priceIndex)}/m2`
  };
};

export default {
  PricingContext,
  PricingProvider,
  PricingConsumer,
  usePricingContext,
  useDisplayListingPricing,
  useDisplayPriceIndex
};
